import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, Package, Plus, Loader2 } from "lucide-react";
import { API } from "./Purchaseshared";
import axios from "axios";
import toast from "react-hot-toast";
import { productNameOf, productImageUrl } from "./Purchaseshared";

/**
 * Pick a catalogue product from a searchable dropdown, or type an item by hand
 * when it isn't in the catalogue.
 *
 * value:    { product, name, sku, isManual }
 * onChange: receives a PARTIAL patch of the same shape
 * takenIds: Set of product ids already used in other rows (shown as "Already added")
 */
const Thumb = ({ product, size = 36 }) => {
    const [failed, setFailed] = useState(false);
    const src = productImageUrl(product);
    return (
        <span
            style={{ width: size, height: size }}
            className="shrink-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
        >
            {src && !failed ? (
                <img src={src} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" />
            ) : (
                <Package size={size * 0.5} className="text-slate-300" />
            )}
        </span>
    );
};

const searchText = (p) => {
    const names = p.name && typeof p.name === "object" ? Object.values(p.name).join(" ") : p.name || "";
    return `${names} ${p.sku || ""}`.toLowerCase();
};

const ProductPicker = ({ products, setProducts, value, onChange, takenIds, invalid }) => {
    const uid = useRef(`pp-${Math.random().toString(36).slice(2, 8)}`).current;
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const nameRef = useRef(null);
    const focusNameNext = useRef(false);

    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [creating, setCreating] = useState(false);

    const selected = useMemo(
        () => (value.product ? products.find((p) => p._id === value.product) : null),
        [products, value.product]
    );

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? products.filter((p) => searchText(p).includes(q)) : products;
        return list.slice(0, 50);
    }, [products, query]);

    // last option is always "enter manually"
    const options = useMemo(
        () => [...results.map((p) => ({ type: "product", p })), { type: "manual" }],
        [results]
    );

    /* close on outside click */
    useEffect(() => {
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    /* keep the highlighted option visible */
    useEffect(() => {
        if (!open) return;
        document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView({ block: "nearest" });
    }, [active, open, uid]);

    /* focus the name box right after switching to manual entry */
    useEffect(() => {
        if (value.isManual && focusNameNext.current) {
            nameRef.current?.focus();
            focusNameNext.current = false;
        }
    }, [value.isManual]);

    const pickProduct = (p) => {
        onChange({ product: p._id, name: productNameOf(p), sku: p.sku || "", isManual: false });
        setQuery("");
        setOpen(false);
    };

    const instantCreate = async () => {
        const name = query.trim();
        if (!name) return;
        
        setCreating(true);
        try {
            const res = await axios.post(`${API}/purchases/instant-product`, { name });
            const newProduct = res.data.product;
            setProducts(prev => [newProduct, ...prev]);
            
            onChange({ product: newProduct._id, name: productNameOf(newProduct), sku: newProduct.sku, isManual: false });
            setQuery("");
            setOpen(false);
            toast.success("Product created and selected!");
        } catch (error) {
            toast.error("Could not add product instantly.");
            console.error(error);
        } finally {
            setCreating(false);
        }
    };

    const backToCatalogue = () => onChange({ product: "", name: "", sku: "", isManual: false });

    const choose = (opt) => {
        if (!opt) return;
        if (opt.type === "manual") return instantCreate();
        if (takenIds?.has(opt.p._id)) return;
        pickProduct(opt.p);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, options.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && open) {
            e.preventDefault();
            choose(options[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const borderCls = invalid
        ? "border-red-400 focus:ring-red-200"
        : "border-slate-300 focus:ring-[#d4af37]/50 focus:border-transparent";

    /* ---------------- manual mode (removed) ---------------- */

    /* ---------------- catalogue product chosen ---------------- */
    if (value.product) {
        return (
            <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white px-3 py-2">
                <Thumb product={selected} size={40} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{selected ? productNameOf(selected) : value.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                        SKU: {selected?.sku || value.sku || "--"}
                        {typeof selected?.stock === "number" && ` · In stock: ${selected.stock}`}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        backToCatalogue();
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    className="text-xs font-medium text-blue-600 hover:underline shrink-0"
                >
                    Change
                </button>
            </div>
        );
    }

    /* ---------------- search box ---------------- */
    return (
        <div ref={wrapRef} className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    setActive(0);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="Search product by name or SKU…"
                role="combobox"
                aria-expanded={open}
                aria-controls={`${uid}-list`}
                aria-activedescendant={open ? `${uid}-opt-${active}` : undefined}
                aria-autocomplete="list"
                aria-invalid={invalid || undefined}
                className={`w-full rounded-lg border pl-9 pr-9 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 ${borderCls}`}
            />
            {query && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                        setQuery("");
                        inputRef.current?.focus();
                    }}
                    className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                    <X size={16} />
                </button>
            )}

            {open && (
                <ul
                    id={`${uid}-list`}
                    role="listbox"
                    className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl py-1"
                >
                    {results.length === 0 && (
                        <li className="px-3 py-2.5 text-sm text-slate-500" role="presentation">
                            {products.length === 0 ? "No products in the catalogue yet." : "No product matches your search."}
                        </li>
                    )}

                    {options.map((opt, i) => {
                        const isActive = i === active;
                        if (opt.type === "manual") {
                            return (
                                <li
                                    key="manual"
                                    id={`${uid}-opt-${i}`}
                                    role="option"
                                    aria-selected={isActive}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => choose(opt)}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer border-t border-slate-100 ${isActive ? "bg-amber-50" : "bg-white"
                                        }`}
                                >
                                    <span className="w-9 h-9 shrink-0 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
                                        <Plus size={16} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block font-medium text-white truncate">
                                            {query.trim() ? `Add “${query.trim()}” to catalogue instantly` : "Can't find it? Type a name to add it"}
                                        </span>
                                        <span className="block text-xs text-slate-500">Will be saved to database</span>
                                    </span>
                                </li>
                            );
                        }

                        const { p } = opt;
                        const taken = takenIds?.has(p._id);
                        return (
                            <li
                                key={p._id}
                                id={`${uid}-opt-${i}`}
                                role="option"
                                aria-selected={isActive}
                                aria-disabled={taken || undefined}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(opt)}
                                className={`flex items-center gap-2.5 px-3 py-2 text-sm ${taken ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                    } ${isActive && !taken ? "bg-slate-100" : "bg-white"}`}
                            >
                                <Thumb product={p} />
                                <span className="min-w-0 flex-1">
                                    <span className="block font-medium text-white truncate">{productNameOf(p)}</span>
                                    <span className="block text-xs text-slate-500 truncate">SKU: {p.sku || "--"}</span>
                                </span>
                                <span className="shrink-0 text-xs text-slate-500">
                                    {taken ? "Already added" : typeof p.stock === "number" ? `Stock ${p.stock}` : ""}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default ProductPicker;